import { Fragment, useEffect } from "react";
import { FC } from "react";
import { Box } from "@mui/material";
import { Navigate, Route, useLocation, useNavigate } from "react-router-dom";
import { AdRoutes } from "../../constants/ad-routes";
import { makeSxStyles } from "ui";
import CustomerDetailPage from "./customer-detail/customer-detail-page";
import Sidebar from "./components/sidebar";
import SnowReportsPage from "./snow-reports/snow-reports-page";
import AddAddressPage from "./add-address/add-address-page";
import "react-toastify/dist/ReactToastify.css";
import RoutePlannerPage from "./route-planner/[date]";
import CreateTempRouteSequencePage from "./route-planner/create-temp-route/create-temp-route-sequence-page";
import CreateTempRouteDetailsPage from "./route-planner/create-temp-route/create-temp-route-details-page";
import SnowReportsDetailPage from "./snow-reports-detail/snow-reports-detail-page";

import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Outlet,
} from "react-router-dom";
import SnowReportsCreatePage from "./snow-reports-create/snow-reports-create-page";
import NotificationsManagementPage from "./notifications-management/notifications-management-page";
import AddNotificationPage from "./add-notification/add-notification-page";
import EditNotificationPage from "./add-notification/edit-notification-page";

import CustomerManagementComboPage from "./customer-management-combo/address-management-page";
import { default as NotificationRecipientsPage } from "../../features/notifications/pages/notification-recipients-page";
import AddCustomerComboPage from "./add-customer-combo/add-customer-combo-page";
import CreateSequencePage from "./region-management/create-sequence/create-sequence-page";
import FleetManagementPage from "./fleet-management/fleet-management-page";
import AddVehiclePage from "./add-vehicle/add-vehicle-page";
import DriverManagementPage from "./driver-management/driver-management-page";
import AddDriverPage from "./add-driver/add-driver-page";
import DriverDetailsPage from "./driver-details/driver-details-page";
import RegionManagementPage from "./region-management/region-management-page";
import CreateRegionPage from "./region-management/create-region/create-region-page";
import VehicleTypesPage from "./fleet-management/vehicle-type-page";
import AddVehicleTypePage from "./add-vehicle-type/add-vehicle-type";
import EditServiceAlertPage from "./add-notification/edit-service-alert-page";
import EditEventNotificationPage from "../../features/notifications/pages/edit-event-notification-page";
import Navbar from "../../components/navbar/navbar";
import ImpersonationBanner from "../../shared/components/impersonation-banner/impersonation-banner";
import SocketContextProvider from "../../web-sockets/web-socket-provider";
import CsvImportModal from "../../components/csv-import-modal/csv-import-modal";
import useCsvUploadStore from "../../state/csv-upload-store";
import MapProvider from "../../components/map/map-provider";
import LeadManagementPage from "../../features/leads/pages/LeadManagementPage";
import AddLeadPage from "../../features/leads/pages/AddLeadPage";
import EditLeadPage from "../../features/leads/pages/EditLeadPage";
import ConvertLeadPage from "../../features/leads/pages/ConvertLeadPage";
import RouteGuard from "../../shared/components/route-guard";
import OrganizationManagementPage from "../../features/organizations/pages/OrganizationManagementPage";
import AddOrganizationPage from "../../features/organizations/pages/AddOrganizationPage";
import OrganizationDetailPage from "../../features/organizations/pages/OrganizationDetailPage";
import EditOrganizationPage from "../../features/organizations/pages/EditOrganizationPage";
import ReplyEmailSetupPage from "../../features/settings/pages/ReplyEmailSetupPage";
import SettingsPage from "../../features/settings/pages/SettingsPage";
import { SettingsTabEnum } from "../../features/settings/constants/settings-tabs";
import { consumePendingPostAuthRedirect } from "../../features/settings/utils/pending-post-auth-redirect";
import useTopOffset from "../../hooks/use-top-offset";

const leads_enabled = import.meta.env.VITE_LEADS_ENABLED === "true";

const Layout = () => {
  const styles = useStyles();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isModalOpen, modalStatus, reviewData, closeModal } =
    useCsvUploadStore();
  const topOffset = useTopOffset();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Runs once, right after login flips the app over to DashboardRouter (see
  // login-page.tsx / accept-invite-page.tsx / pending-post-auth-redirect.ts).
  // Using this router's own navigate() - not the one from AuthRouter, which
  // can't reach DashboardRouter's separate router instance.
  useEffect(() => {
    const pendingRedirect = consumePendingPostAuthRedirect();
    if (pendingRedirect) navigate(pendingRedirect, { replace: true });
  }, []);

  const isCustomerManagementPage = pathname.includes(AdRoutes.customer);

  const layoutContent = (
    <Fragment>
      <ImpersonationBanner />
      <Navbar showAccountMenu />
      <Box
        sx={{ ...styles.container, minHeight: `calc(100vh - ${topOffset}px)` }}
      >
        <Sidebar />
        <Box
          flexGrow={1}
          marginLeft={8}
          marginTop={`${topOffset}px`}
          maxWidth="calc(100% - 56px)"
        >
          <MapProvider>
            <Outlet />
          </MapProvider>
        </Box>
      </Box>
      <CsvImportModal
        open={isModalOpen}
        status={modalStatus}
        reviewData={reviewData || undefined}
        onClose={closeModal}
      />
    </Fragment>
  );

  // Wrap with SocketContextProvider for customer management pages
  if (isCustomerManagementPage) {
    return (
      <SocketContextProvider currentPage="customer-management">
        {layoutContent}
      </SocketContextProvider>
    );
  }

  return layoutContent;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* Bare route, deliberately outside <Layout> - no sidebar/navbar chrome,
          matching the Figma full-screen signup step. Still requires an
          authenticated session (RouteGuard with no `allow` just checks
          isLoggedIn), reached only from accept-invite-page.tsx's onSuccess. */}
      <Route
        path={AdRoutes.setupReplyEmail}
        element={
          <RouteGuard>
            <ReplyEmailSetupPage />
          </RouteGuard>
        }
      />
      <Route
        path="/"
        element={
          <RouteGuard>
            <Layout />
          </RouteGuard>
        }
      >
        <Route index element={<Navigate to={AdRoutes.customer} />} />
        <Route
          path={AdRoutes.customer}
          element={<CustomerManagementComboPage />}
        />
        <Route
          path={AdRoutes.customerDetailAddress}
          element={<CustomerDetailPage />}
        />
        <Route
          path={AdRoutes.customerDetail}
          element={<CustomerDetailPage />}
        />
        <Route
          path={AdRoutes.addCustomerCombo}
          element={<AddCustomerComboPage />}
        />
        <Route path={AdRoutes.addAddress} element={<AddAddressPage />} />
        <Route path={AdRoutes.snowReports} element={<SnowReportsPage />} />
        <Route
          path={AdRoutes.snowReportsCreate}
          element={<SnowReportsCreatePage />}
        />
        <Route
          path={AdRoutes.snowReportsDetail}
          element={<SnowReportsDetailPage />}
        />
        {/* "Change password" moved into the Settings screen (ESR-1616);
            redirect any bookmarked/old link there instead of leaving it
            dangling. */}
        <Route
          path={AdRoutes.updatePassword}
          element={
            <Navigate
              to={`${AdRoutes.settings}?tab=${SettingsTabEnum.password}`}
              replace
            />
          }
        />
        <Route path={AdRoutes.settings} element={<SettingsPage />} />
        <Route path={AdRoutes.routePlanner} element={<RoutePlannerPage />} />
        <Route
          path={AdRoutes.createTempRouteSequence}
          element={<CreateTempRouteSequencePage />}
        />
        <Route
          path={AdRoutes.createTempRouteDetails}
          element={<CreateTempRouteDetailsPage />}
        />
        <Route
          path={AdRoutes.notifications}
          element={<NotificationsManagementPage />}
        />
        <Route
          path={AdRoutes.addNotification}
          element={<AddNotificationPage />}
        />
        <Route
          path={AdRoutes.notificationDetails}
          element={<EditNotificationPage />}
        />
        <Route
          path={AdRoutes.notificationServiceAlertDetails}
          element={<EditServiceAlertPage />}
        />
        <Route
          path={AdRoutes.notificationRecipients}
          element={<NotificationRecipientsPage />}
        />
        <Route
          path={AdRoutes.editEventNotification}
          element={<EditEventNotificationPage />}
        />
        <Route
          path={AdRoutes.fleetManagement}
          element={<FleetManagementPage />}
        />
        <Route path={AdRoutes.addVehicle} element={<AddVehiclePage />} />
        <Route path={AdRoutes.editVehicle} element={<AddVehiclePage />} />
        <Route path={AdRoutes.vehicleTypes} element={<VehicleTypesPage />} />
        <Route
          path={AdRoutes.addVehicleType}
          element={<AddVehicleTypePage />}
        />
        <Route
          path={AdRoutes.editVehicleType}
          element={<AddVehicleTypePage />}
        />

        <Route
          path={AdRoutes.driverManagement}
          element={<DriverManagementPage />}
        />
        <Route path={AdRoutes.addDriver} element={<AddDriverPage />} />
        <Route path={AdRoutes.editDriver} element={<DriverDetailsPage />} />
        <Route
          path={AdRoutes.regionManagement}
          element={<RegionManagementPage />}
        />
        <Route
          path={AdRoutes.createSequence}
          element={<CreateSequencePage />}
        />
        <Route
          path={AdRoutes.resequenceRoute}
          element={<CreateSequencePage />}
        />
        <Route
          path={AdRoutes.sequenceUnassigned}
          element={<CreateSequencePage />}
        />
        <Route
          path={AdRoutes.createRegionDetails}
          element={<CreateRegionPage />}
        />
        <Route
          path={AdRoutes.editRegionDetails}
          element={<CreateRegionPage />}
        />
        {leads_enabled && (
          <Fragment>
            <Route path={AdRoutes.leads} element={<LeadManagementPage />} />
            <Route path={AdRoutes.leadAdd} element={<AddLeadPage />} />
            <Route path={AdRoutes.leadConvert} element={<ConvertLeadPage />} />
            <Route path={AdRoutes.leadDetail} element={<EditLeadPage />} />
          </Fragment>
        )}
        <Route
          path={AdRoutes.organizations}
          element={
            <RouteGuard allow={({ isSuperAdmin }) => isSuperAdmin}>
              <OrganizationManagementPage />
            </RouteGuard>
          }
        />
        <Route
          path={AdRoutes.addOrganization}
          element={
            <RouteGuard allow={({ isSuperAdmin }) => isSuperAdmin}>
              <AddOrganizationPage />
            </RouteGuard>
          }
        />
        <Route
          path={AdRoutes.editOrganization}
          element={
            <RouteGuard allow={({ isSuperAdmin }) => isSuperAdmin}>
              <EditOrganizationPage />
            </RouteGuard>
          }
        />
        <Route
          path={AdRoutes.organizationDetail}
          element={
            <RouteGuard allow={({ isSuperAdmin }) => isSuperAdmin}>
              <OrganizationDetailPage />
            </RouteGuard>
          }
        />
        <Route path="*" element={<Navigate to={AdRoutes.customer} />} />
      </Route>
    </>,
  ),
);

const DashboardRouter: FC = () => {
  return <RouterProvider router={router} />;
};

const useStyles = makeSxStyles(() => ({
  container: {
    minHeight: "calc(100vh - 56px)",
    width: "100%",
    display: "flex",
    flexDirection: "row",
  },
}));

export default DashboardRouter;
